import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';

import { AccountSelectors } from '@recruitee/account';
import { filterBoolean } from '@recruitee/common';
import type { Company } from '@recruitee/company-types';
import { I18nService } from '@recruitee/i18n';
import type { Country } from '@recruitee/locations-types';

import { injectCountryList, injectCountryMap } from './country-data-helpers';

@Injectable({ providedIn: 'root' })
export class CountrySelectOptionsService {
  private companyCountryCode: string;
  private countriesMap: Record<string, Country> = injectCountryMap();
  private countries: Country[] = injectCountryList();

  constructor(
    private i18n: I18nService,
    private store: Store,
  ) {
    this.store
      .select(AccountSelectors.currentCompany)
      .pipe(filterBoolean)
      .subscribe(({ countryCode }: Company) => {
        this.companyCountryCode = countryCode!;
      });
  }

  public getAllCountrySelectOptions() {
    return this.countries.map(country => ({
      label: country.name,
      value: country.code,
    }));
  }

  public getGroupedCountrySelectOptions() {
    const country = this.countriesMap[this.companyCountryCode];
    const preferredCountry = country
      ? {
          label: country.name,
          value: country.code,
          additionalSearchKeys: [country.code],
        }
      : null;

    const preferredGroup = {
      title: null,
      options: preferredCountry ? [preferredCountry] : [],
    };

    const allCountriesGroup = {
      title: preferredGroup.options.length
        ? this.i18n.translate('app.offer_form.all_countries')
        : null,
      options: this.countries
        .filter(c => c.code !== preferredCountry?.value)
        .map(country => ({
          label: country.name,
          value: country.code,
          additionalSearchKeys: [country.code],
        })),
    };

    return [preferredGroup, allCountriesGroup];
  }
}
